import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { IconName, Spinner, Stack } from '@optimus-web/core';
import { DataTestId } from '@mews-commander/enums';
import { useAddFavoriteQuery } from '../query/useAddFavoriteQuery';
import { useUpdateFavoriteQuery } from '../query/useUpdateFavoriteQuery';
import { AssistantSearchListItemProps } from '../drawer/hooks/useAssistantSearchResultListMap';
import { SearchBarIconButton } from './styles';

interface SearchInputRightElementProps {
    searchTerm: string;
    showRecent?: boolean;
    loading: boolean;
    favoriteListItems: Array<AssistantSearchListItemProps>;
    selectedFavoritePrompt?: AssistantSearchListItemProps;
    setSelectedFavoritePrompt: Dispatch<SetStateAction<AssistantSearchListItemProps | undefined>>;
}

export const SearchInputRightElement = ({
    searchTerm,
    showRecent,
    loading,
    favoriteListItems,
    selectedFavoritePrompt,
    setSelectedFavoritePrompt,
}: SearchInputRightElementProps) => {
    const [isFavorite, setIsFavorite] = useState(false);

    const { mutate: addFavorite, isPending: addFavoriteProcessing } = useAddFavoriteQuery();
    const { mutate: removeFavorite, isPending: removeFavoriteProcessing } = useUpdateFavoriteQuery();

    useEffect(() => {
        if (searchTerm?.length > 0) {
            const favoritePromptItem = favoriteListItems.find(
                (item) => item.value?.toLowerCase() === searchTerm?.trimEnd().toLowerCase()
            );
            if (favoritePromptItem) {
                setIsFavorite(true);
                setSelectedFavoritePrompt(favoritePromptItem);
            } else {
                setIsFavorite(false);
                setSelectedFavoritePrompt(undefined);
            }
        }
    }, [searchTerm, favoriteListItems, setSelectedFavoritePrompt]);

    const handleAddFavorite = useCallback(
        (event: React.MouseEvent) => {
            if (searchTerm?.length > 0) {
                addFavorite({ isFavorite: true, prompt: searchTerm?.trimEnd() });
                setIsFavorite(true);
            }
            event.stopPropagation();
        },
        [addFavorite, searchTerm]
    );

    const handleRemoveFavorite = useCallback(
        (event: React.MouseEvent, favoritePrompt: AssistantSearchListItemProps) => {
            if (favoritePrompt.itemId && favoritePrompt.value) {
                removeFavorite({ id: favoritePrompt.itemId, prompt: favoritePrompt.value, isFavorite: false });
                setIsFavorite(false);
            }
            event.stopPropagation();
        },
        [removeFavorite]
    );

    return (
        <Stack alignment="center" itemAlignment="center" spacing="50">
            {!showRecent && !addFavoriteProcessing && !removeFavoriteProcessing ? (
                <>
                    {isFavorite && selectedFavoritePrompt ? (
                        <SearchBarIconButton
                            variant="ghost"
                            icon={IconName.StarFilled}
                            data-test-id={DataTestId.AssistantAddFavoriteSearch}
                            iconSize="small"
                            type="button"
                            onClick={(e) => handleRemoveFavorite(e, selectedFavoritePrompt)}
                        />
                    ) : null}
                    {!isFavorite ? (
                        <SearchBarIconButton
                            variant="ghost"
                            type="button"
                            icon={IconName.Stars}
                            data-test-id={DataTestId.AssistantAddFavoriteSearch}
                            iconSize="small"
                            onClick={handleAddFavorite}
                        />
                    ) : null}
                </>
            ) : null}
            {loading || addFavoriteProcessing || removeFavoriteProcessing ? <Spinner size="small" /> : null}
        </Stack>
    );
};
